import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-picker-with-range';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminLogs } from '@/hooks/useAnalytics';
import { formatDistanceToNow, format } from 'date-fns';
import { Activity, User, FileText, Settings, Shield, Download, Search, Filter } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { usePagination } from '@/hooks/usePagination';
import PaginationControls from '@/components/ui/pagination-controls';

const getActionIcon = (actionType: string) => {
  switch (actionType.toLowerCase()) {
    case 'user_management':
      return <User className="h-4 w-4" />;
    case 'content_management':
      return <FileText className="h-4 w-4" />;
    case 'system_settings':
      return <Settings className="h-4 w-4" />;
    case 'security':
      return <Shield className="h-4 w-4" />;
    default:
      return <Activity className="h-4 w-4" />;
  }
};

const getActionColor = (actionType: string) => {
  switch (actionType.toLowerCase()) {
    case 'user_management':
      return 'bg-white text-black';
    case 'content_management':
      return 'bg-white text-green-800';
    case 'system_settings':
      return 'bg-white text-black';
    case 'security':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-white text-black';
  }
};

const EnhancedAdminLogs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('all');
  const [adminFilter, setAdminFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const { data: logs, isLoading } = useAdminLogs(1000);

  const filteredLogs = logs?.filter((log: any) => {
    const matchesSearch = 
      log.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.admin?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.admin?.username?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesActionType = actionTypeFilter === 'all' || log.action_type === actionTypeFilter;
    
    const matchesAdmin = adminFilter === 'all' || log.admin_id === adminFilter;
    
    const matchesDateRange = !dateRange?.from || (
      new Date(log.created_at) >= dateRange.from &&
      (!dateRange.to || new Date(log.created_at) <= dateRange.to)
    );

    return matchesSearch && matchesActionType && matchesAdmin && matchesDateRange;
  }) || [];

  const {
    currentPage,
    totalPages,
    goToPage,
    getPageItems
  } = usePagination({
    totalItems: filteredLogs.length,
    itemsPerPage,
    initialPage: 1
  });

  const paginatedLogs = getPageItems(filteredLogs);

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'Action Type', 'Admin', 'Description', 'Target Type'].join(','),
      ...filteredLogs.map((log: any) => [
        format(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss'),
        log.action_type,
        log.admin?.full_name || log.admin?.username || 'Unknown',
        `"${log.description.replace(/"/g, '""')}"`,
        log.target_type || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const uniqueActionTypes = [...new Set(logs?.map((log: any) => log.action_type) || [])];
  const uniqueAdmins = [...new Set(logs?.map((log: any) => log.admin_id) || [])];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-white rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Enhanced Admin Activity Log
            <Badge variant="outline">{filteredLogs.length} logs</Badge>
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportLogs}
              disabled={filteredLogs.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black" />
              <Input
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/20 rounded-lg">
            <div>
              <label className="text-sm font-medium mb-2 block">Action Type</label>
              <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All action types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All action types</SelectItem>
                  {uniqueActionTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Admin</label>
              <Select value={adminFilter} onValueChange={setAdminFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All admins" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All admins</SelectItem>
                  {uniqueAdmins.map((adminId) => {
                    const admin = logs?.find((log: any) => log.admin_id === adminId)?.admin;
                    return (
                      <SelectItem key={adminId} value={adminId}>
                        {admin?.full_name || admin?.username || 'Unknown'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Date Range</label>
              <DatePickerWithRange
                date={dateRange}
                onDateChange={setDateRange}
              />
            </div>
          </div>
        )}

        {paginatedLogs.length > 0 ? (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedLogs.map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action_type)}
                          <Badge className={`text-xs ${getActionColor(log.action_type)}`}>
                            {log.action_type.replace('_', ' ')}
                          </Badge>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="font-medium">
                          {log.admin?.full_name || log.admin?.username || 'Unknown'}
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="max-w-xs truncate" title={log.description}>
                          {log.description}
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        {log.target_type && (
                          <Badge variant="outline" className="text-xs">
                            {log.target_type}
                          </Badge>
                        )}
                      </TableCell>
                      
                      <TableCell className="text-sm text-black">
                        <div title={format(new Date(log.created_at), 'PPpp')}>
                          {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={filteredLogs.length}
              onPageChange={goToPage}
              onItemsPerPageChange={setItemsPerPage}
              showItemsPerPage={true}
            />
          </>
        ) : (
          <div className="text-center py-8">
            <Activity className="h-12 w-12 text-black mx-auto mb-4" />
            <p className="text-black">
              {logs?.length === 0 ? 'No admin activity logs found' : 'No logs match your search criteria'}
            </p>
            {(searchTerm || actionTypeFilter !== 'all' || adminFilter !== 'all' || dateRange) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setActionTypeFilter('all');
                  setAdminFilter('all');
                  setDateRange(undefined);
                }}
                className="mt-2"
              >
                Clear filters
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EnhancedAdminLogs;
